import { useMemo } from "react";
import type { Column } from "@legal-500/legal500-design-system";
import { DataTable } from "../../../components/DataTable/DataTable.js";
import type { ApplyRowResult, ApplyRowStatus } from "../../../api/contactSlugBackfill.service.js";
import styles from "./ContactSlugBackfill.module.css";

/** The per-contact outcome table shown after a slug backfill run. */

export const STATUS_LABELS: Record<ApplyRowStatus, string> = {
  generated: "Generated",
  skipped_not_empty: "Skipped — not empty",
  skipped_unslugifiable: "Skipped — unslugifiable",
  not_found: "Not found",
};

const PILL_CLASSES: Record<ApplyRowStatus, string> = {
  generated: styles.pillOk as string,
  // Warnings, not errors: a skip is the endpoint refusing to write — the
  // contact already holds a slug, or still cannot be slugged — rather than
  // something having gone wrong mid-write.
  skipped_not_empty: styles.pillWarning as string,
  skipped_unslugifiable: styles.pillWarning as string,
  not_found: styles.pillError as string,
};

/** Tallies results by status, for the summary tiles. */
export function countByStatus(results: ApplyRowResult[]): (status: ApplyRowStatus) => number {
  const tally = new Map<ApplyRowStatus, number>();
  for (const result of results) {
    tally.set(result.status, (tally.get(result.status) ?? 0) + 1);
  }
  return status => tally.get(status) ?? 0;
}

type ResultsTableProps = {
  results: ApplyRowResult[];
  /** Resolves a contact name when the apply result carries none. */
  nameFor: (result: ApplyRowResult) => string | null;
};

type TableRow = {
  id: string;
  name: string | null;
  contactId: string;
  slug: string | null;
  status: ApplyRowStatus;
};

export function ResultsTable({ results, nameFor }: ResultsTableProps) {
  const rows = useMemo<TableRow[]>(
    () =>
      results.map((result, index) => ({
        // Index-prefixed: a malformed selection could repeat a contactId.
        id: `${index}-${result.contactId}`,
        name: result.name ?? nameFor(result),
        contactId: result.contactId,
        slug: result.slug ?? null,
        status: result.status,
      })),
    [results, nameFor],
  );

  const columns = useMemo<Column<TableRow>[]>(
    () => [
      {
        header: "Contact",
        accessor: "name",
        wrap: true,
        render: (_value, row) => <span className={styles.name}>{row.name || "—"}</span>,
      },
      {
        header: "Contact id",
        accessor: "contactId",
        // The first eight characters are enough to tell rows apart by eye; the
        // full id stays a hover away rather than widening every row.
        render: (_value, row) => (
          <span className={styles.mono} title={row.contactId}>
            {row.contactId.slice(0, 8)}
          </span>
        ),
      },
      {
        header: "Status",
        accessor: "status",
        render: (_value, row) => (
          <span className={`${styles.pill} ${PILL_CLASSES[row.status]}`}>
            {STATUS_LABELS[row.status]}
          </span>
        ),
      },
      {
        header: "Generated slug",
        accessor: "slug",
        wrap: true,
        render: (_value, row) => <span className={styles.mono}>{row.slug ?? "—"}</span>,
      },
    ],
    [],
  );

  return <DataTable data={rows} columns={columns} emptyMessage="No results." />;
}
